import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import api from '../../../services/api';

const POPUP_COOLDOWN_MS = 30 * 60 * 1000;

const resolveImage = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
};

const cooldownKey = (userId, tenantSlug) => (
  `login_announcement_popup:last_shown:${tenantSlug || 'main'}:${userId || 'guest'}`
);

const recentlyShown = (key) => {
  try {
    const value = Number(localStorage.getItem(key) || 0);
    return value > 0 && Date.now() - value < POPUP_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const markShown = (key) => {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch {
    /* ignore storage failures */
  }
};

export default function LoginAnnouncementPopup({ userId, tenantSlug }) {
  const scope = `${tenantSlug || 'main'}:${userId || 'guest'}`;
  const [result, setResult] = useState({ scope: '', banners: [] });
  const banners = result.scope === scope ? result.banners : [];
  const [activeIdx, setActiveIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const storageKey = cooldownKey(userId, tenantSlug);

  useEffect(() => {
    if (!userId) return undefined;
    let live = true;
    api.get('/api/promo-banners', {
      params: { placement: 'login', _refresh: Date.now() },
      headers: { 'Cache-Control': 'no-cache' },
    })
      .then((res) => {
        if (!live) return;
        const next = (res.data?.banners || []).filter((banner) => (
          banner?.enabled !== false && banner?.image_only && banner?.image_url
        ));
        setResult({ scope, banners: next });
        setActiveIdx(0);
      })
      .catch(() => {
        if (live) setResult({ scope, banners: [] });
      });
    return () => { live = false; };
  }, [userId, tenantSlug, scope]);

  useEffect(() => {
    if (result.scope !== scope || banners.length === 0) {
      setVisible(false);
      return;
    }
    if (recentlyShown(storageKey)) {
      setVisible(false);
      return;
    }
    markShown(storageKey);
    setVisible(true);
  }, [banners.length, storageKey, result.scope, scope]);

  useEffect(() => {
    if (!visible) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  const markSeenAndClose = () => {
    if (activeIdx < banners.length - 1) {
      setActiveIdx((idx) => idx + 1);
      return;
    }
    setVisible(false);
  };

  if (!visible || result.scope !== scope || banners.length === 0) return null;

  const active = banners[Math.min(activeIdx, banners.length - 1)] || banners[0];

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/86 px-3 py-5 backdrop-blur-md sm:px-6 sm:py-8"
      role="dialog"
      aria-modal="true"
      aria-label="Announcement"
      data-testid="login-announcement-popup"
    >
      <div className="relative w-full" style={{ maxWidth: 'min(1320px, calc(100vw - 18px))' }}>
        <div
          className="relative overflow-hidden rounded-[22px] border border-white/18 bg-[#0b1220] shadow-[0_28px_110px_rgba(0,0,0,0.78)] sm:rounded-[30px]"
          style={{ height: 'clamp(260px, 48vw, 760px)', maxHeight: '78dvh' }}
        >
          <img
            src={resolveImage(active.image_url)}
            alt={active.title || 'Announcement'}
            className="block h-full w-full object-contain object-center"
            draggable="false"
            loading="eager"
            decoding="async"
          />
          <button
            type="button"
            onClick={markSeenAndClose}
            className="absolute right-3 top-3 z-10 grid h-11 w-11 place-items-center rounded-full bg-black/42 text-white shadow-2xl backdrop-blur transition hover:bg-black/62 active:scale-95 sm:right-4 sm:top-4 sm:h-12 sm:w-12"
            aria-label="Close announcement"
            data-testid="login-announcement-close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );
}
